import Authlayout from '@/components/Layouts/AuthLayout';
import DashboardLayout from '@/components/Layouts/DashboardLayout';
import MobileDeviceGate from '@/components/Layouts/MobileDeviceGate';
import Protected from '@/components/Layouts/Protected';
import { OrgUserContextProvider } from '@/components/OrgUsers/OrgUserContext';
import RequestResetPassword from '@/pages/auth/RequestReset';
import ResetPassword from '@/pages/auth/Reset';
import SignIn from '@/pages/auth/SignIn';
import SignUp from '@/pages/auth/SignUp';
import Dashboard from '@/pages/dashboard/Dashboard';
import GamePacksPage from '@/pages/dashboard/GamePack';
import GetStartedPage from '@/pages/GetStarted';
import GameSetup from '@/pages/host/GameSetup';
import Host from '@/pages/host/Host';
import LandingPage from '@/pages/Landing';
import OrgUsers from '@/pages/orgs/OrgUsers';
import Player from '@/pages/player/Player';
import ProfilePage from '@/pages/Profile';
import UnauthorizedPage from '@/pages/Unauthorized';
import { QueryClient, QueryClientProvider } from 'react-query';
import { createBrowserRouter, Navigate, Outlet, RouterProvider } from 'react-router-dom';
import HostSessionWrapper from './components/Layouts/HostSessionWrapper';
import CardView from './pages/dashboard/metrics/Card';
import CardViews from './pages/dashboard/metrics/Cards';
import Metrics from './pages/dashboard/metrics/Metrics';
import Organizations from './pages/orgs/Organizations';
import PlayerSessionWrapper from './components/Layouts/PlayerSessionWrapper';
import PrivacyPage from './pages/Privacy';
import Insights from './pages/dashboard/Insights';
import { OrgContextProvider } from './components/Organizations/OrgContext';
import GuideLayout from './components/Layouts/GuideLayout';
import Guide from './pages/guide/Guide';
import { LayoutContextProvider } from './components/Navigation/LayoutContext';
import Admin from './pages/dashboard/Admin';
import QuickGame from './pages/host/QuickGame';
import FreeTrialSignUp from './pages/auth/FreeTrialSignUp';
import Invite from './pages/invite/Invite';

const queryClient = new QueryClient();

const router = createBrowserRouter([
  {
    path: '/',
    element: <MobileDeviceGate />,
    children: [
      {
        path: '',
        element: <LandingPage />
      },
      {
        path: '/guide',
        element: (
          <Protected>
            <GuideLayout />
          </Protected>
        ),
        children: [
          {
            path: '',
            element: <Guide />
          }
        ]
      },
      {
        path: '/invite',
        element: (
          <Protected>
            <OrgContextProvider>
              <OrgUserContextProvider>
                <GuideLayout hideFreeTrialBanner={true} />
              </OrgUserContextProvider>
            </OrgContextProvider>
          </Protected>
        ),
        children: [
          {
            path: '',
            element: <Invite />
          }
        ]
      },
      {
        path: '/quick-game',
        element: (
          <Protected>
            <GuideLayout />
          </Protected>
        ),
        children: [
          {
            path: '',
            element: <QuickGame />
          }
        ]
      },
      {
        path: '/dashboard',
        element: (
          <Protected>
            <LayoutContextProvider>
              <DashboardLayout />
            </LayoutContextProvider>
          </Protected>
        ),
        children: [
          {
            path: '',
            element: <Dashboard />
          },
          {
            path: 'insights',
            element: <Insights />
          },
          {
            path: 'admin',
            element: <Admin />
          },
          {
            path: 'host',
            element: <GameSetup />
          },
          { path: 'gamepacks', element: <GamePacksPage /> },
          {
            path: 'metrics',
            element: <Metrics />
          },
          {
            path: 'metrics/card/:id',
            element: <CardView />
          },
          {
            path: 'metrics/cards/:id',
            element: <CardViews />
          },
          {
            path: 'orgs',
            element: (
              <OrgContextProvider>
                <OrgUserContextProvider>
                  <Outlet />
                </OrgUserContextProvider>
              </OrgContextProvider>
            ),
            children: [
              {
                path: '',
                element: <Organizations />
              },
              {
                path: ':tenantId/users',
                element: <OrgUsers /> /* here */
              }
            ]
          },
          {
            path: 'users',
            element: (
              <OrgContextProvider>
                <OrgUserContextProvider>
                  <Outlet />
                </OrgUserContextProvider>
              </OrgContextProvider>
            ),
            children: [
              {
                path: '',
                element: <OrgUsers /> /* here */
              }
            ]
          },
          {
            path: 'getting-started',
            element: <GetStartedPage />
          },
          {
            path: 'profile',
            element: <ProfilePage />
          }
        ]
      },
      {
        path: '/unauthorized',
        element: <UnauthorizedPage />
      },
      {
        path: '/auth',
        element: <Authlayout />,
        children: [
          { path: 'sign-up', element: <SignUp /> },
          { path: 'sign-in', element: <SignIn /> },
          { path: 'request-reset', element: <RequestResetPassword /> },
          { path: 'reset', element: <ResetPassword /> },
          { path: 'free-trial', element: <FreeTrialSignUp /> }
        ]
      },
      {
        path: '/session/:id/host',
        element: (
          <HostSessionWrapper>
            <Protected>
              <Host />
            </Protected>
          </HostSessionWrapper>
        )
      },
      {
        path: '/session/:id/player',
        element: (
          <PlayerSessionWrapper>
            <Player />
          </PlayerSessionWrapper>
        )
      },
      {
        path: '/privacy-policy',
        element: <PrivacyPage />
      },
      {
        path: '*',
        element: <Navigate to="/" replace />
      }
    ]
  }
]);

function Router() {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}

export default Router;
